import { useMemo, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  ThemeProvider,
  createTheme,
  CssBaseline,
  CircularProgress,
} from "@mui/material";
import { EnrollmentFormData } from "./enrollmentModels";
import { IChangeEvent } from "@rjsf/core";
import { host } from "config";
import { convertJSONToMultiPartFormData } from "../utils/general";
import FormSubmittedSplashPage from "../formSubmittedSplashPage";
import { coverageSelectionSchema, dependentInfoSchema, groupInfoSchema, primaryInfoSchema } from "./formSchema";
import { groupInfoUiSchema } from "../groupImplementation/uiSchema";
import { buildUiSchemaWithShrunkLabels } from "../utils/uiSchemaHelper";
import { coverageSelectionUiSchema, dependentInfoUiSchema, primaryInfoUiSchema } from "./uiSchema";
import img from "../assets/657507_tmpl_head_60db3e2024ea6.png";
import Form from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import styles from "../form.module.css";

// Create MUI theme matching the form style
const theme = createTheme({
  palette: {
    primary: {
      main: "#2196f3",
    },
    background: {
      default: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"Open Sans", Arial, sans-serif',
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
        InputLabelProps: {
          shrink: true,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        root: {
          alignItems: "flex-start",
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          marginLeft: 0,
          width: "100%",
          marginTop: 0,
          marginBottom: 0,
        },
        label: {
          marginLeft: "8px", // spacing between radio and text
          textAlign: "left",
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          PaperProps: {
            style: {
              maxHeight: 300,
              maxWidth: 300,
            },
          },
        },
      },
      styleOverrides: {
        select: {
          width: "700px",
          boxSizing: "border-box",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "block",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          alignItems: "flex-start",
          paddingTop: 8,
          paddingBottom: 8,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          width: "100%",
        },
        input: {
          minWidth: 0,
          overflow: "hidden",
        },
      },
    },
  },
});

const EnrollmentForm: React.FC = () => {
  const [formData, setFormData] = useState<EnrollmentFormData | undefined>();
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSubmittedPage, setShowSubmittedPage] = useState(false);
  const [page, setPage] = useState(0);

  const dynamicGroupInfoUiSchema = useMemo(() => {
    const baseGroupInfoUiSchema = { ...groupInfoUiSchema };

    return buildUiSchemaWithShrunkLabels(groupInfoSchema, baseGroupInfoUiSchema);
  }, [formData]);

  const dynamicPrimaryInfoUiSchema = useMemo(() => {
    const basePrimaryInfoUiSchema = { ...primaryInfoUiSchema };

    return buildUiSchemaWithShrunkLabels(primaryInfoSchema, basePrimaryInfoUiSchema);
  }, [formData]);

  const dynamicCoverageSelectionUiSchema = useMemo(() => {
    const baseCoverageSelectionUiSchema = { ...coverageSelectionUiSchema };

    return buildUiSchemaWithShrunkLabels(coverageSelectionSchema, baseCoverageSelectionUiSchema);
  }, [formData]);

  const dynamicDependentInfoUiSchema = useMemo(() => {
    const baseDependendentInfoUiSchema = { ...dependentInfoUiSchema };

    baseDependendentInfoUiSchema.dependents["ui:options"] = {
      ...baseDependendentInfoUiSchema.dependents["ui:options"],
      removable: (formData?.dependents?.length ?? 0) > 1,
    };

    return buildUiSchemaWithShrunkLabels(dependentInfoSchema, baseDependendentInfoUiSchema);
  }, [formData]);

  const handleSubmit = (data: IChangeEvent<EnrollmentFormData>) => {
    const cleanedData = { ...formData };

    cleanedData.dependents.forEach((dep) => {
      if (dep.useSameAddressAsPrimary) {
        dep.address1 = cleanedData.address1;
        dep.address2 = cleanedData.address2;
        dep.city = cleanedData.city;
        dep.state = cleanedData.state;
        dep.zipCode = cleanedData.zipCode;
      }
    });

    setIsLoading(true);
    fetch(`${host}/api/implementation/forms/enrollment`, {
      method: "POST",
      body: JSON.stringify(cleanedData),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          response.text().then((text) => {
            throw new Error(text);
          });
        } else {
          response.json().then(async (data: any) => {
            setIsLoading(false);

            setShowSubmittedPage(true);
          });
        }
      })
      .catch((error: any) => {
        console.error("Error:", error);
        alert(
          "There was an error while submitting the form. Please try again later and contact IT if the problem persists."
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChange = (data: IChangeEvent<EnrollmentFormData>) => {
    setFormData(data.formData);
  };

  const handleError = (errors) => {
    console.log("Form errors:", errors);
  };

  if (isLoading) {
    return (
      <div className="loader-container">
        <CircularProgress />
      </div>
    );
  }

  if (showSubmittedPage) {
    return <FormSubmittedSplashPage formName="Enrollment" />;
  }

  const steps = [
    {
      title: "Group Information",
      schema: groupInfoSchema,
      uiSchema: dynamicGroupInfoUiSchema,
    },
    {
      title: "Primary Member Information",
      schema: primaryInfoSchema,
      uiSchema: dynamicPrimaryInfoUiSchema,
    },
    {
      title: "Coverage Selection",
      description: "Please provide your plan name or plan ID and coverage tier.",
      schema: coverageSelectionSchema,
      uiSchema: dynamicCoverageSelectionUiSchema,
    },
    {
      title: "Dependent Information",
      schema: dependentInfoSchema,
      uiSchema: dynamicDependentInfoUiSchema,
    },
  ];

  const progress = (page / (steps.length - 1)) * 100;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: "#ffffff",
            borderRadius: 2,
            border: "2px solid #000",
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <img
              src={img}
              alt="OptiMed Health"
              style={{
                maxWidth: "350px",
                height: "auto",
                display: "block",
                margin: "0 auto",
              }}
            />
          </Box>
          {!showForm ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography sx={{ mb: 2 }}>
                Welcome to OptiMedHealth. This form will guide you through selecting your coverage tier and registering
                your dependents. To ensure a smooth application process, please have the following information ready:
              </Typography>
              <Box sx={{ textAlign: "left" }}>
                <ul>
                  <li>Your Group Name and Group Number. </li>
                  <li>
                    Full legal names, Dates of Birth, and Social Security Numbers for yourself and all applicable
                    dependents.
                  </li>
                  <li>Your current residential address.</li>
                </ul>
              </Box>
              <Typography sx={{ mb: 4 }}>
                Need help? If you are missing your group information or have questions about your eligibility, please
                contact us a customercare@optimedhealth.com or call 800-836-6036
              </Typography>
              <Button
                variant="contained"
                onClick={() => setShowForm(true)}
                sx={{
                  backgroundColor: "#5a6c7d",
                  color: "white",
                  px: 4,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "150px",
                  "&:hover": {
                    backgroundColor: "#4a5c6d",
                  },
                }}
              >
                Start
              </Button>
            </Box>
          ) : (
            <>
              {/* header */}
              <Box
                sx={{
                  backgroundColor: "#1a365d",
                  color: "white",
                  p: 3,
                  mb: 3,
                  textAlign: "center",
                  borderRadius: 2,
                }}
              >
                <Typography variant="h5">{steps[page]?.title}</Typography>
                {steps[page]?.description && (
                  <Typography variant="body1" sx={{ color: "white", mt: 3, whiteSpace: "pre-line" }}>
                    {steps[page]?.description}
                  </Typography>
                )}
              </Box>

              {/* form */}
              <Form
                schema={steps[page]?.schema}
                uiSchema={steps[page]?.uiSchema}
                formData={formData}
                validator={validator}
                onChange={handleChange}
                onSubmit={
                  page === steps.length - 1 ||
                  (page === steps.length - 2 && formData?.coverageSelection === "Employee Only")
                    ? handleSubmit
                    : () => setPage((p) => p + 1)
                }
                onError={handleError}
                showErrorList="top"
                className={styles["custom-form"]}
              >
                {/* nav */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 3,
                    width: "100%",
                  }}
                >
                  {/* left */}
                  <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
                    <Button
                      variant="contained"
                      disabled={page === 0}
                      onClick={() => setPage((p) => p - 1)}
                      sx={{
                        backgroundColor: "#5a6c7d",
                        color: "white",
                        px: 3,
                        py: 1.2,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        minWidth: "150px",
                        border: "none",
                        "&:hover": {
                          backgroundColor: "#4a5c6d",
                          border: "none",
                        },
                      }}
                    >
                      Back
                    </Button>
                  </Box>

                  {/* center */}
                  {/* Page indicator with progress bar - center */}
                  <Box
                    sx={{
                      flex: "1 1 auto",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      px: 2,
                      pb: 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5, whiteSpace: "nowrap", fontSize: "0.875rem" }}
                    >
                      {page + 2} of {steps.length + 1}
                    </Typography>
                    {/* Progress bar */}
                    <Box
                      sx={{
                        width: "200px",
                        height: "4px",
                        backgroundColor: "#d3d3d3",
                        borderRadius: "2px",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${progress}%`,
                          height: "100%",
                          backgroundColor: "#808080",
                          transition: "width 0.3s ease",
                        }}
                      />
                    </Box>
                  </Box>

                  {/* right */}
                  <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      type="submit"
                      sx={{
                        backgroundColor: "#5a6c7d",
                        color: "white",
                        px: 3,
                        py: 1.2,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        minWidth: "150px",
                        border: "none",
                        "&:hover": {
                          backgroundColor: "#4a5c6d",
                          border: "none",
                        },
                      }}
                    >
                      {page === steps.length - 1 ||
                      (page === steps.length - 2 && formData?.coverageSelection === "Employee Only")
                        ? "Submit"
                        : "Next"}
                    </Button>
                  </Box>
                </Box>
              </Form>

              {/* footer */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="caption">* Required fields must be completed</Typography>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default EnrollmentForm;
